import CardSchedule from "@components/shared-components/Card/CardSchedule"
import ModalVerifyAccount from "@components/shared-components/Modal/VerifyAccount"
import Portal from "@components/shared-components/Portal"
import Badge from "@components/shared-components/ui/Badge"
import {
  cutString,
  formatStringToDateFormat,
  formatToIDR,
  generateImageWebp,
  optimizeImageCloudinary,
  roundValueRating,
} from "lib/helpers"
import Button from "lib/components/Button"
import Divider from "lib/components/Divider"
import ImageComponent from "lib/components/Image"
import RatingComponent from "lib/components/Rating"
import Text from "lib/components/Text"
import { LanguageContext } from "context/LanguageContext"
import _ from "lodash"
import { useContext, useState } from "react"
import useCardDetailProgram from "./useCardDetailProgram"

interface HeaderProps {
  programThumbnailUrl: string
  isActive: boolean
  customComponent: React.ReactNode
  type: "inhouse" | "public" | "bootcamp" | "prakerja" | "preview"
  isImageHeaderRounded: boolean
}

function Header({
  programThumbnailUrl,
  isActive,
  customComponent,
  type,
  isImageHeaderRounded,
}: HeaderProps) {
  const { useTranslation: t } = useContext(LanguageContext)
  const customImage = generateImageWebp(programThumbnailUrl)
  const thumbnailUrl = optimizeImageCloudinary(customImage, "high", "w_640")

  return (
    <div className="relative aspect-[16/11] overflow-hidden">
      {type !== "preview" &&
        <div>
          {!isActive && (
            <div className="absolute bottom-0 z-10 w-full bg-green-100 p-2">
              <Text size="small" weight="bold" color="secondary" alignText="center">
                {t(`this_${type === "inhouse" ? "program" : "class"}_has_been_completed`)}
              </Text>
            </div>
          )}
        </div>
      }
      <div className="aspect-[16/11] w-full relative">
        <ImageComponent
          src={thumbnailUrl || "/assets/images/program-image.webp"}
          alt={programThumbnailUrl}
          objectFit="cover"
          placeholder="blur"
          rounded={isImageHeaderRounded}
        />
      </div>
      {customComponent && isActive && (
        <div className="absolute top-0  right-0 z-10 h-9">{customComponent}</div>
      )}
    </div>
  )
}
function Prices({ prices }) {
  return (
    <div className="row-span-1 flex items-center justify-between border-t p-4 gap-x-1">
      {prices?.[0]?.discount > 0 && (
        <div className="rounded bg-[#FCDDDE] px-2 py-1">
          <Text customClass="leading-4" color="danger" size="small" weight="semibold">
            {prices?.[0].discount || 0}%
          </Text>
        </div>
      )}

      <Text customClass="leading-5" color="primary" size="large" weight="bold">
        {prices?.[0].discount_price < 1 ? "FREE" : formatToIDR(prices?.[0].discount_price)}
      </Text>
      {prices?.[0].discount > 0 && (
        <Text size="small" weight="semibold" color="muted" customClass="line-through">
          {prices?.[0].price ? formatToIDR(prices?.[0].price) : 0}
        </Text>
      )}
    </div>
  )
}
function Ratings({ rating, ratingParticipants }) {
  return (
    <div className="flex items-center space-x-4 p-4">
        <Text color="gray" customClass="leading-5" weight="normal" size="small">
          {ratingParticipants === 0
            ? ratingParticipants
            : roundValueRating(parseFloat(ratingParticipants).toFixed(1))}
        </Text>
        {ratingParticipants > 0 && (
          <RatingComponent
            size={13}
            isHalf
            value={roundValueRating(parseFloat(ratingParticipants).toFixed(1))}
            count={5}
          />
        )}
        <Text color="gray" customClass="leading-5" weight="normal" size="small">
          {`(${
            rating > 100
              ? `${Math.round(rating)}+ Ratings`
              : `${Math.round(rating)} Rating${rating > 1 ? "s" : ""}`
          })`}
        </Text>
      </div>
  )
}
function Schedules({ classDetail, type, offlineDelivery, onlineDelivery }) {
  const { useTranslation: t } = useContext(LanguageContext)
  const isStartDateEndDateSame = classDetail?.start_date === classDetail?.end_date

  const startDate = {
    date: formatStringToDateFormat(classDetail?.start_date, "DD"),
    years: formatStringToDateFormat(classDetail?.start_date, "YYYY"),
    translateDay: isStartDateEndDateSame
      ? `${t(formatStringToDateFormat(classDetail?.start_date, "dddd").toLowerCase())},`
      : "",
    translateMonth: t(
      formatStringToDateFormat(classDetail?.start_date, "MMMM").toLowerCase(),
    ).slice(0, !isStartDateEndDateSame ? 3 : 20),
  }
  const endDate = {
    date: formatStringToDateFormat(classDetail?.end_date, "DD"),
    years: formatStringToDateFormat(classDetail?.end_date, "YYYY"),
    translateDay: isStartDateEndDateSame
      ? `${t(formatStringToDateFormat(classDetail?.end_date, "dddd").toLowerCase())},`
      : "",
    translateMonth: t(formatStringToDateFormat(classDetail?.end_date, "MMMM").toLowerCase()).slice(
      0,
      3,
    ),
  }
  const simplifyStartDate = `${startDate.translateDay} ${startDate.date} ${startDate.translateMonth}  ${startDate.years}`
  const simplifyEndDate = `${endDate.translateDay} ${endDate.date} ${endDate.translateMonth}  ${endDate.years}`
  const scheduleType = {
    public: `${simplifyStartDate} ${!isStartDateEndDateSame ? ` - ${simplifyEndDate}` : ""}`,
    bootcamp: `${simplifyStartDate} - ${simplifyEndDate}`,
  }
  return (
    <div className="flex flex-row items-center p-4">
      {classDetail?.delivery_method ? (
        <CardSchedule
          label={`${classDetail?.delivery_method}`}
          schedule={scheduleType?.[type]}
          iconSize={20}
          size="extra-small"
        />
      ) : (
        <div className="flex space-x-4">
          {offlineDelivery && <Badge label="offline" customClass="bg-pink-100 text-pink-600" />}

          {onlineDelivery && <Badge label="online" customClass="bg-indigo-100 text-indigo-600" />}
        </div>
      )}
    </div>
  )
}
function CheckoutOrContactUs({ type, onClick, isActive }) {
  const { useTranslation: t } = useContext(LanguageContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)

  if (type === "inhouse") {
    return (
      <div className="flex items-center py-2 lg:px-4">
        <Button
          variant="contained"
          color="primary"
          customClass="w-full h-12 lg:hidden block"
          onClick={onClick}
          loading={isLoading}
          disabled={!isActive}
          size="small"
          iconRight="qiscus-chat"
          iconType="stroke"
          iconColor="white"
          rounded="large">
          {cutString(t("chat_with_us"), 15)}
        </Button>
        <Button
          variant="contained"
          color="primary"
          customClass="w-full h-12 lg:block hidden"
          onClick={onClick}
          loading={isLoading}
          disabled={!isActive}
          size="small"
          iconRight="qiscus-chat"
          iconType="stroke"
          iconColor="white"
          rounded="large">
          {t("chat_with_us")}
        </Button>
      </div>
    )
  }

  if (type === "prakerja") {
    return (
      <div className="flex items-center py-2 lg:px-4">
        <Button
          variant="contained"
          color="muted"
          customClass="w-full h-12"
          rounded="large"
          iconRight="union-right"
          size="small">
          Coming Soon
        </Button>
      </div>
    )
  }

  if (type === "public") {
    return (
      <div className="flex items-center py-2 lg:px-4">
        <Button
          variant="contained"
          color="primary"
          customClass="w-full h-12"
          rounded="large"
          loading={isLoading}
          disabled={!isActive}
          iconRight="union-right"
          size="small"
          onClick={() => {
            onClick()
            setIsLoading(true)
          }}>
          {t("buy_ticket")}
        </Button>
      </div>
    )
  }

  return (
    <div className="flex items-center py-2 lg:px-4">
      <Button
        variant="contained"
        color="primary"
        customClass="w-full h-12"
        rounded="large"
        disabled
        iconRight="union-right"
        size="small">
        {t("buy_ticket")}
      </Button>
    </div>
  )
}

function PortalPrice({ prices }) {
  return (
    <div>
      <Text customClass="leading-5" color="primary" size="large" weight="bold">
        {prices?.[0].discount_price < 1 ? "FREE" : formatToIDR(prices?.[0].discount_price)}
      </Text>
      <div className="flex items-center space-x-2 pt-2">
        {prices?.[0]?.discount > 0 && (
          <div className="rounded bg-[#FCDDDE] px-1.5 py-1">
            <Text customClass="leading-4" color="danger" size="small" weight="semibold">
              {`${prices?.[0].discount}%`}
            </Text>
          </div>
        )}
        {prices?.[0].discount > 0 && (
          <Text size="small" weight="semibold" color="muted" customClass="line-through">
            {prices?.[0].price ? formatToIDR(prices?.[0].price) : 0}
          </Text>
        )}
      </div>
    </div>
  )
}
interface CardDetailProgramProps {
  id: string
  prices: Array<{
    price: number
    discount: number
    discount_price: number
  }>
  rating: number
  showVerifyModal?: boolean
  loadingVerify?: boolean
  ratingParticipants: number
  program?: {
    program_thumbnail_url?: string
    type?: string
  }
  onlineDelivery: boolean
  offlineDelivery: boolean
  classDetail: {
    delivery_method: string
    start_date: string
    campaign?: string
  }
  customClassImage?: {
    image_url: string
  }[]
  isActive?: boolean
  customComponent?: React.ReactNode
  handleVerifyAccount: () => void
  handleCheckout?: () => void
  handleToggleVerifyModal?: () => void
  type: "inhouse" | "public" | "bootcamp" | "prakerja" | "preview"
}

function CardDetailProgram({
  id,
  prices,
  rating,
  ratingParticipants,
  program,
  classDetail,
  customClassImage,
  isActive,
  handleToggleVerifyModal,
  offlineDelivery,
  onlineDelivery,
  customComponent,
  handleCheckout,
  handleVerifyAccount,
  showVerifyModal,
  loadingVerify,
  type,
}: CardDetailProgramProps) {
  const { isImageHeaderRounded } = useCardDetailProgram()
  const { useTranslation: t } = useContext(LanguageContext)
  const { program_thumbnail_url: programThumbnailUrl } = program
  const priceType = {
    inhouse: [_.minBy(prices, "price")],
    public: [{ price: _.minBy(prices, "price")?.price, ...classDetail }],
    bootcamp: [{ price: _.minBy(prices, "price")?.price, ...classDetail }],
    prakerja: [{ price: _.minBy(prices, "price")?.price, ...classDetail }],
  }

  // variable for help styling
  const roundedLg = isImageHeaderRounded ? "rounded-lg" : ""

  let typeProgram = type
  if (classDetail.campaign === "prakerja") {
    typeProgram = "prakerja"
  }

  return (
    <div key={id} className={`overflow-hidden ${roundedLg} hover:cursor-pointer lg:shadow `}>
      {/* header */}
      <Header
        customComponent={customComponent}
        programThumbnailUrl={
          customClassImage?.length > 0 ? customClassImage[0].image_url : programThumbnailUrl
        }
        isActive={isActive}
        type={typeProgram}
        isImageHeaderRounded={isImageHeaderRounded}
      />
      {/* header */}

      {/* body */}
      <div className="hidden lg:block">
        {/* prices */}
        <Prices prices={priceType?.[typeProgram !== "preview" ? typeProgram : "inhouse"]} />
        {/* prices */}
        <Divider
          customClass={`border-gray-100 my-0 border-t-0 ${rating > 0 ? "block" : "hidden"}`}
        />
        {/* Rating */}
        {rating > 0 && <Ratings rating={rating} ratingParticipants={ratingParticipants} />}
        {/* Rating */}
        <Divider customClass="border-gray-300 my-0 border-t-0" />
        {/* schedules */}
        <Schedules
          classDetail={classDetail}
          type={typeProgram}
          offlineDelivery={offlineDelivery}
          onlineDelivery={onlineDelivery}
        />
        {/* schedules */}
        <Divider customClass="border-gray-300 my-0 border-t-0" />
        {/* button */}
        {isActive && (
          <CheckoutOrContactUs type={typeProgram} onClick={handleCheckout} isActive={isActive} />
        )}
        {/* button */}

        <Divider customClass="border-gray-300 my-0 border-t-0" />
      </div>
      {/* body */}

      {/* card payment fixed footer */}
      <Portal>
        <div className="fixed left-0 bottom-0 z-40 flex w-screen items-center justify-between border-t bg-white px-4 py-2 shadow-lg lg:hidden">
          <PortalPrice prices={priceType?.[typeProgram || "inhouse"]} />
          <div>
            <CheckoutOrContactUs type={typeProgram} onClick={handleCheckout} isActive={isActive} />
          </div>
        </div>
      </Portal>
      {showVerifyModal && (
        <ModalVerifyAccount
          description={t("you_cannot_continue_order_because_your_account_is_not_verified")}
          onClose={handleToggleVerifyModal}
          onVerify={handleVerifyAccount}
          loadingVerify={loadingVerify}
        />
      )}
    </div>
  )
}

export default CardDetailProgram
